import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const LayoutCustomizer = ({ 
  selectedLayout, 
  onLayoutSelect, 
  selectedSections, 
  onSectionToggle,
  timeFormat,
  onTimeFormatChange,
  timeRange,
  onTimeRangeChange,
  selectedHabits,
  onHabitsChange,
  className = "" 
}) => {
  const [rangeError, setRangeError] = useState('');
  
  // New state for component-based selection
  const [componentSelection, setComponentSelection] = useState({
    yearlyOverview: true, // Always true, locked
    monthPlanner: false,
    weekPlanner: false,
    dailyPlanner: {
      enabled: false,
      schedule: false,
      task: false,
      combination: true, // Default when dailyPlanner enabled
      journal: {
        enabled: false,
        type: 'no_headings', // 'no_headings' | 'subject_headings'
        headings: ['', '', ''] // 3 custom headings
      }
    },
    lifeHack: {
      enabled: false,
      components: {
        mood: false,
        water: false,
        weather: false,
        meal: false
      }
    },
    meetingNotes: false, // Disabled
    notes: false // Disabled
  });
  
  const [showDailyOptions, setShowDailyOptions] = useState(false);
  const [showJournalOptions, setShowJournalOptions] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Generate time options for dropdowns (3 AM to 12 AM)
  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 3; hour <= 24; hour++) {
      const hour12 = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
      const period = hour < 12 ? 'AM' : (hour === 24 ? 'AM' : 'PM');
      const displayHour = hour === 24 ? 12 : hour12;
      
      options?.push({
        value: hour === 24 ? 0 : hour,
        label: `${displayHour}:00 ${period}`
      });
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  // Generate filtered options with proper safety checks and fallbacks
  const getStartTimeOptions = () => {
    const endValue = timeRange?.end !== undefined ? timeRange?.end : 18; // Default to 6 PM
    const filtered = timeOptions?.filter(opt => {
      const optValue = opt?.value === 0 ? 24 : opt?.value; // Treat 0 as 24 for midnight
      const endComparison = endValue === 0 ? 24 : endValue;
      return optValue >= 3 && optValue < endComparison;
    });
    
    // Fallback: if filtering results in empty array, return all valid start times (3 AM to 11 PM)
    if (filtered?.length === 0) {
      return timeOptions?.filter(opt => {
        const optValue = opt?.value === 0 ? 24 : opt?.value;
        return optValue >= 3 && optValue <= 23;
      });
    }
    
    return filtered;
  };

  const getEndTimeOptions = () => {
    const startValue = timeRange?.start !== undefined ? timeRange?.start : 6; // Default to 6 AM
    const filtered = timeOptions?.filter(opt => {
      const optValue = opt?.value === 0 ? 24 : opt?.value; // Treat 0 as 24 for midnight
      return optValue > startValue && optValue <= 24;
    });
    
    // Fallback: if filtering results in empty array, return all valid end times (4 AM to midnight)
    if (filtered?.length === 0) {
      return timeOptions?.filter(opt => {
        const optValue = opt?.value === 0 ? 24 : opt?.value;
        return optValue >= 4 && optValue <= 24;
      });
    }
    
    return filtered;
  };

  // Validate and adjust time range
  const handleStartTimeChange = (startValue) => {
    let start = parseInt(startValue);
    let end = timeRange?.end;

    // Auto-adjust end time if invalid range
    if (start >= end) {
      end = Math.min(start + 8, 24);
      setRangeError(`End time auto-adjusted to ${formatTime(end)} to maintain minimum 8-hour range`);
      
      // Clear error after 5 seconds
      setTimeout(() => setRangeError(''), 5000);
    } else {
      setRangeError('');
    }

    onTimeRangeChange({ start, end });
  };

  const handleEndTimeChange = (endValue) => {
    let end = parseInt(endValue);
    let start = timeRange?.start;

    // Auto-adjust start time if invalid range
    if (end <= start) {
      start = Math.max(end - 8, 3);
      setRangeError(`Start time auto-adjusted to ${formatTime(start)} to maintain minimum 8-hour range`);
      
      // Clear error after 5 seconds
      setTimeout(() => setRangeError(''), 5000);
    } else {
      setRangeError('');
    }

    onTimeRangeChange({ start, end });
  };

  // Format time for error messages
  const formatTime = (hour) => {
    const hour12 = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
    const period = hour < 12 ? 'AM' : (hour === 24 ? 'AM' : 'PM');
    const displayHour = hour === 24 ? 12 : hour12;
    return `${displayHour}:00 ${period}`;
  };

  // Validate at least one main component selected
  const validateMainComponents = (selection) => {
    const hasMainComponent = 
      selection?.monthPlanner || 
      selection?.weekPlanner || 
      selection?.dailyPlanner?.enabled;
    
    if (!hasMainComponent) {
      setValidationError('Please select at least one main component besides Yearly Overview');
      return false;
    }
    
    setValidationError('');
    return true;
  };

  // Handle main component checkbox changes
  const handleComponentChange = (componentId, checked) => {
    const newSelection = { ...componentSelection };
    
    if (componentId === 'monthPlanner') {
      newSelection.monthPlanner = checked;
    } else if (componentId === 'weekPlanner') {
      newSelection.weekPlanner = checked;
    } else if (componentId === 'dailyPlanner') {
      newSelection.dailyPlanner.enabled = checked;
      setShowDailyOptions(checked);
      
      // If unchecking, reset all daily sub-options
      if (!checked) {
        newSelection.dailyPlanner.schedule = false;
        newSelection.dailyPlanner.task = false;
        newSelection.dailyPlanner.combination = true;
        newSelection.dailyPlanner.journal.enabled = false;
        setShowJournalOptions(false);
      }
    } else if (componentId === 'lifeHack') {
      newSelection.lifeHack.enabled = checked;
      
      // Enable/disable life hack checkboxes in Additional Sections
      if (!checked) {
        newSelection.lifeHack.components = {
          mood: false,
          water: false,
          weather: false,
          meal: false
        };
        // Update Additional Sections state
        onSectionToggle('mood', false);
        onSectionToggle('water', false);
        onSectionToggle('weather', false);
        onSectionToggle('meals', false);
      }
    }
    
    setComponentSelection(newSelection);
    validateMainComponents(newSelection);
    
    // Pass data to parent for PDF generator
    onLayoutSelect(newSelection);
  };

  // Handle daily planner sub-options
  const handleDailySubOption = (optionId, checked) => {
    const newSelection = { ...componentSelection };
    
    if (optionId === 'schedule') {
      newSelection.dailyPlanner.schedule = checked;
      if (checked) {
        newSelection.dailyPlanner.combination = false;
      }
    } else if (optionId === 'task') {
      newSelection.dailyPlanner.task = checked;
      if (checked) {
        newSelection.dailyPlanner.combination = false;
      }
    } else if (optionId === 'combination') {
      newSelection.dailyPlanner.combination = checked;
      if (checked) {
        newSelection.dailyPlanner.schedule = false;
        newSelection.dailyPlanner.task = false;
      }
    } else if (optionId === 'journal') {
      newSelection.dailyPlanner.journal.enabled = checked;
      setShowJournalOptions(checked);
    }
    
    // If all daily sub-options are unchecked, uncheck daily planner
    const hasAnyDailyOption = 
      newSelection?.dailyPlanner?.schedule || 
      newSelection?.dailyPlanner?.task || 
      newSelection?.dailyPlanner?.combination || 
      newSelection?.dailyPlanner?.journal?.enabled;
    
    if (!hasAnyDailyOption) {
      newSelection.dailyPlanner.enabled = false;
      setShowDailyOptions(false);
    }
    
    setComponentSelection(newSelection);
    validateMainComponents(newSelection);
    onLayoutSelect(newSelection);
  };

  // Handle journal heading type change
  const handleJournalTypeChange = (type) => {
    const newSelection = { ...componentSelection };
    newSelection.dailyPlanner.journal.type = type;
    
    // FIXED: Clear headings array when switching to no_headings
    if (type === 'no_headings') {
      newSelection.dailyPlanner.journal.headings = [];
    } else {
      // Initialize with minimum 2 empty headings for subject_headings
      newSelection.dailyPlanner.journal.headings = ['', ''];
    }
    
    setComponentSelection(newSelection);
    onLayoutSelect(newSelection);
    
    // Clear validation error when switching types
    setValidationError('');
  };

  // Handle custom journal heading input
  const handleJournalHeadingChange = (index, value) => {
    const newSelection = { ...componentSelection };
    
    // FIXED: Enforce 75 character limit
    const trimmedValue = value?.substring(0, 75);
    
    // Initialize headings array if needed
    if (!newSelection?.dailyPlanner?.journal?.headings) {
      newSelection.dailyPlanner.journal.headings = [];
    }
    
    // Ensure array has enough elements
    while (newSelection?.dailyPlanner?.journal?.headings?.length <= index) {
      newSelection?.dailyPlanner?.journal?.headings?.push('');
    }
    
    newSelection.dailyPlanner.journal.headings[index] = trimmedValue;
    
    setComponentSelection(newSelection);
    onLayoutSelect(newSelection);
    
    // Validate headings
    validateJournalHeadings(newSelection);
  };
  
  // NEW: Validate journal headings
  const validateJournalHeadings = (selection) => {
    if (selection?.dailyPlanner?.journal?.enabled && 
        selection?.dailyPlanner?.journal?.type === 'subject_headings') {
      
      const headings = selection?.dailyPlanner?.journal?.headings || [];
      const filledHeadings = headings?.filter(h => h?.trim()?.length > 0);
      
      // FIXED: Require minimum 2 headings with text
      if (filledHeadings?.length < 2) {
        setValidationError('Please enter at least 2 subject headings for the journal page');
        return false;
      }
      
      // Check character limits
      const hasOverLimit = headings?.some(h => h?.length > 75);
      if (hasOverLimit) {
        setValidationError('Subject headings must be less than 75 characters');
        return false;
      }
      
      setValidationError('');
      return true;
    }
    
    setValidationError('');
    return true;
  };
  
  // NEW: Add heading input field
  const handleAddHeading = () => {
    const newSelection = { ...componentSelection };
    if (!newSelection?.dailyPlanner?.journal?.headings) {
      newSelection.dailyPlanner.journal.headings = [];
    }
    
    // Maximum 4 headings
    if (newSelection?.dailyPlanner?.journal?.headings?.length < 4) {
      newSelection?.dailyPlanner?.journal?.headings?.push('');
      setComponentSelection(newSelection);
      onLayoutSelect(newSelection);
    }
  };
  
  // NEW: Remove heading input field
  const handleRemoveHeading = (index) => {
    const newSelection = { ...componentSelection };
    if (newSelection?.dailyPlanner?.journal?.headings?.length > 2) {
      newSelection?.dailyPlanner?.journal?.headings?.splice(index, 1);
      setComponentSelection(newSelection);
      onLayoutSelect(newSelection);
      validateJournalHeadings(newSelection);
    }
  };

  // Handle life hack component toggles
  const handleLifeHackComponent = (componentId, checked) => {
    const newSelection = { ...componentSelection };
    newSelection.lifeHack.components[componentId] = checked;
    setComponentSelection(newSelection);
    onLayoutSelect(newSelection);
    
    // Also toggle in Additional Sections
    const sectionMap = {
      mood: 'mood',
      water: 'water',
      weather: 'weather',
      meal: 'meals'
    };
    onSectionToggle(sectionMap?.[componentId], checked);
  };

  const sectionOptions = [
    { id: 'habits', name: 'Habit Tracker', icon: 'CheckSquare', description: 'Track daily habits and routines' }
  ];

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Component-Based Selection - REPLACES Layout Selection */}
      <div className="space-y-4">
        <h3 className="font-heading text-lg font-semibold text-foreground">
          Choose Components to Include
        </h3>
        <p className="font-body text-sm text-muted-foreground">
          Select the components you'd like to include in your planner
        </p>
        
        {/* Validation Error */}
        {validationError && (
          <div className="flex items-start space-x-2 p-3 bg-error/10 border border-error rounded-lg">
            <Icon name="AlertCircle" size={16} className="text-error mt-0.5" />
            <p className="text-sm text-error">{validationError}</p>
          </div>
        )}
        
        <div className="space-y-3">
          {/* 1. Yearly Overview (locked) */}
          <div className="p-4 rounded-lg border-2 border-primary bg-primary/5">
            <div className="flex items-start space-x-3">
              <Checkbox
                checked
                disabled
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <Icon name="Calendar" size={20} className="text-primary" />
                  <h4 className="font-heading font-semibold text-foreground">
                    Yearly Overview
                  </h4>
                  <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded">
                    Required
                  </span>
                </div>
                <p className="font-caption text-sm text-muted-foreground mt-1">
                  Full year calendar with all 12 months
                </p>
              </div>
            </div>
          </div>

          {/* 2. Month Planner */}
          <div className="p-4 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors">
            <div className="flex items-start space-x-3">
              <Checkbox
                checked={componentSelection?.monthPlanner}
                onChange={(e) => handleComponentChange('monthPlanner', e?.target?.checked)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <Icon name="Calendar" size={20} className="text-muted-foreground" />
                  <h4 className="font-heading font-semibold text-foreground">
                    Month Planner
                  </h4>
                </div>
                <p className="font-caption text-sm text-muted-foreground mt-1">
                  Monthly calendar views for all 12 months
                </p>
              </div>
            </div>
          </div>

          {/* 3. Week Planner */}
          <div className="p-4 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors">
            <div className="flex items-start space-x-3">
              <Checkbox
                checked={componentSelection?.weekPlanner}
                onChange={(e) => handleComponentChange('weekPlanner', e?.target?.checked)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <Icon name="CalendarDays" size={20} className="text-muted-foreground" />
                  <h4 className="font-heading font-semibold text-foreground">
                    Week Planner
                  </h4>
                </div>
                <p className="font-caption text-sm text-muted-foreground mt-1">
                  Weekly planning pages for all 52 weeks
                </p>
              </div>
            </div>
          </div>

          {/* 4. Daily Planner (with expandable sub-options) */}
          <div className="p-4 rounded-lg border border-border bg-card">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Checkbox
                  checked={componentSelection?.dailyPlanner?.enabled}
                  onChange={(e) => handleComponentChange('dailyPlanner', e?.target?.checked)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <Icon name="CalendarCheck" size={20} className="text-muted-foreground" />
                    <h4 className="font-heading font-semibold text-foreground">
                      Daily Planner
                    </h4>
                    {componentSelection?.dailyPlanner?.enabled && (
                      <button
                        onClick={() => setShowDailyOptions(!showDailyOptions)}
                        className="ml-auto text-primary hover:text-primary/80 transition-colors"
                      >
                        <Icon name={showDailyOptions ? 'ChevronUp' : 'ChevronDown'} size={20} />
                      </button>
                    )}
                  </div>
                  <p className="font-caption text-sm text-muted-foreground mt-1">
                    Daily pages for all 365 days
                  </p>
                </div>
              </div>

              {/* Expandable Daily Sub-Options */}
              {componentSelection?.dailyPlanner?.enabled && showDailyOptions && (
                <div className="ml-8 space-y-3 pt-3 border-t border-border">
                  {/* 4a) Daily Schedule */}
                  <div className="flex items-start space-x-3 p-3 rounded-lg bg-muted/30">
                    <Checkbox
                      checked={componentSelection?.dailyPlanner?.schedule}
                      onChange={(e) => handleDailySubOption('schedule', e?.target?.checked)}
                      disabled={componentSelection?.dailyPlanner?.combination}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <h5 className="font-body font-medium text-foreground">
                        Daily Schedule Page
                      </h5>
                      <p className="font-caption text-xs text-muted-foreground mt-1">
                        Time-based schedule layout (unchecks combination)
                      </p>
                    </div>
                  </div>

                  {/* 4b) Daily Task */}
                  <div className="flex items-start space-x-3 p-3 rounded-lg bg-muted/30">
                    <Checkbox
                      checked={componentSelection?.dailyPlanner?.task}
                      onChange={(e) => handleDailySubOption('task', e?.target?.checked)}
                      disabled={componentSelection?.dailyPlanner?.combination}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <h5 className="font-body font-medium text-foreground">
                        Daily Task Page
                      </h5>
                      <p className="font-caption text-xs text-muted-foreground mt-1">
                        Task list layout (unchecks combination)
                      </p>
                    </div>
                  </div>

                  {/* 4c) Daily Schedule and Task Combination (default) */}
                  <div className="flex items-start space-x-3 p-3 rounded-lg bg-primary/5 border border-primary">
                    <Checkbox
                      checked={componentSelection?.dailyPlanner?.combination}
                      onChange={(e) => handleDailySubOption('combination', e?.target?.checked)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h5 className="font-body font-medium text-foreground">
                          Daily Schedule and Task Combination
                        </h5>
                        <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded">
                          Default
                        </span>
                      </div>
                      <p className="font-caption text-xs text-muted-foreground mt-1">
                        Combined schedule and task layout (unchecks 4a and 4b)
                      </p>
                    </div>
                  </div>

                  {/* 4d) Daily Journal Page */}
                  <div className="p-3 rounded-lg bg-muted/30 space-y-3">
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        checked={componentSelection?.dailyPlanner?.journal?.enabled}
                        onChange={(e) => handleDailySubOption('journal', e?.target?.checked)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h5 className="font-body font-medium text-foreground">
                            Daily Journal Page
                          </h5>
                          {componentSelection?.dailyPlanner?.journal?.enabled && (
                            <button
                              onClick={() => setShowJournalOptions(!showJournalOptions)}
                              className="ml-auto text-primary hover:text-primary/80 transition-colors"
                            >
                              <Icon name={showJournalOptions ? 'ChevronUp' : 'ChevronDown'} size={16} />
                            </button>
                          )}
                        </div>
                        <p className="font-caption text-xs text-muted-foreground mt-1">
                          Journaling space for daily reflections
                        </p>
                      </div>
                    </div>

                    {/* Journal Sub-Options */}
                    {componentSelection?.dailyPlanner?.journal?.enabled && showJournalOptions && (
                      <div className="ml-8 space-y-3 pt-3 border-t border-border">
                        {/* 4d i) No subject headings */}
                        <div
                          onClick={() => handleJournalTypeChange('no_headings')}
                          className={`
                            flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors
                            ${componentSelection?.dailyPlanner?.journal?.type === 'no_headings' ?'border-primary bg-primary/5' :'border-border bg-card'
                            }
                          `}
                        >
                          <div className={`
                            w-4 h-4 rounded-full border-2 flex items-center justify-center
                            ${componentSelection?.dailyPlanner?.journal?.type === 'no_headings' ?'border-primary bg-primary' :'border-muted-foreground'
                            }
                          `}>
                            {componentSelection?.dailyPlanner?.journal?.type === 'no_headings' && (
                              <div className="w-2 h-2 rounded-full bg-white" />
                            )}
                          </div>
                          <div className="flex-1">
                            <h6 className="font-body text-sm font-medium text-foreground">
                              No Subject Headings
                            </h6>
                            <p className="font-caption text-xs text-muted-foreground">
                              Lines for entire page (free-form journaling)
                            </p>
                          </div>
                        </div>

                        {/* 4d ii) Subject headings */}
                        <div
                          onClick={() => handleJournalTypeChange('subject_headings')}
                          className={`
                            flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors
                            ${componentSelection?.dailyPlanner?.journal?.type === 'subject_headings' ?'border-primary bg-primary/5' :'border-border bg-card'
                            }
                          `}
                        >
                          <div className={`
                            w-4 h-4 rounded-full border-2 flex items-center justify-center
                            ${componentSelection?.dailyPlanner?.journal?.type === 'subject_headings' ?'border-primary bg-primary' :'border-muted-foreground'
                            }
                          `}>
                            {componentSelection?.dailyPlanner?.journal?.type === 'subject_headings' && (
                              <div className="w-2 h-2 rounded-full bg-white" />
                            )}
                          </div>
                          <div className="flex-1">
                            <h6 className="font-body text-sm font-medium text-foreground">
                              Subject Headings
                            </h6>
                            <p className="font-caption text-xs text-muted-foreground">
                              Custom sections with headings (e.g., "Gratitude", "Areas for improvement")
                            </p>
                          </div>
                        </div>

                        {/* Custom Heading Inputs */}
                        {componentSelection?.dailyPlanner?.journal?.type === 'subject_headings' && (
                          <div className="ml-8 space-y-2 pt-2">
                            <div className="flex items-center justify-between mb-2">
                              <p className="font-caption text-xs text-muted-foreground">
                                Enter 2-4 custom section headings (max 75 characters each):
                              </p>
                              {(componentSelection?.dailyPlanner?.journal?.headings?.length || 0) < 4 && (
                                <button
                                  onClick={handleAddHeading}
                                  className="text-xs text-primary hover:text-primary/80 transition-colors flex items-center space-x-1"
                                >
                                  <Icon name="Plus" size={12} />
                                  <span>Add Heading</span>
                                </button>
                              )}
                            </div>
                            
                            {(componentSelection?.dailyPlanner?.journal?.headings || ['', ''])?.map((heading, index) => (
                              <div key={index} className="flex items-center space-x-2">
                                <span className="font-caption text-xs text-muted-foreground whitespace-nowrap">
                                  {index + 1}.
                                </span>
                                <Input
                                  placeholder={`e.g., ${index === 0 ? 'Gratitude' : index === 1 ? 'Areas for improvement' : index === 2 ? 'Devotional thought' : 'Daily reflection'}`}
                                  value={heading || ''}
                                  onChange={(e) => handleJournalHeadingChange(index, e?.target?.value)}
                                  className="text-sm"
                                  maxLength={75}
                                />
                                <span className="text-xs text-muted-foreground whitespace-nowrap">
                                  {(heading?.length || 0)}/75
                                </span>
                                {index >= 2 && (
                                  <button
                                    onClick={() => handleRemoveHeading(index)}
                                    className="text-error hover:text-error/80 transition-colors"
                                  >
                                    <Icon name="X" size={16} />
                                  </button>
                                )}
                              </div>
                            ))}
                            
                            {validationError?.includes('journal') && (
                              <div className="flex items-start space-x-2 p-2 bg-error/10 border border-error rounded-lg mt-2">
                                <Icon name="AlertCircle" size={14} className="text-error mt-0.5" />
                                <p className="text-xs text-error">{validationError}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* 4e) Life Hack Page */}
                  <div className="p-3 rounded-lg bg-muted/30 space-y-3">
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        checked={componentSelection?.lifeHack?.enabled}
                        onChange={(e) => handleComponentChange('lifeHack', e?.target?.checked)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <h5 className="font-body font-medium text-foreground">
                          Life Hack Page
                        </h5>
                        <p className="font-caption text-xs text-muted-foreground mt-1">
                          Enables life hack components in Additional Sections below
                        </p>
                      </div>
                    </div>

                    {/* Life Hack Components */}
                    {componentSelection?.lifeHack?.enabled && (
                      <div className="ml-8 grid grid-cols-2 gap-2 pt-2 border-t border-border">
                        <div className="flex items-center space-x-2 p-2 rounded bg-card">
                          <Checkbox
                            checked={componentSelection?.lifeHack?.components?.mood}
                            onChange={(e) => handleLifeHackComponent('mood', e?.target?.checked)}
                          />
                          <span className="text-sm">Mood Tracker</span>
                        </div>
                        <div className="flex items-center space-x-2 p-2 rounded bg-card">
                          <Checkbox
                            checked={componentSelection?.lifeHack?.components?.water}
                            onChange={(e) => handleLifeHackComponent('water', e?.target?.checked)}
                          />
                          <span className="text-sm">Water Intake</span>
                        </div>
                        <div className="flex items-center space-x-2 p-2 rounded bg-card">
                          <Checkbox
                            checked={componentSelection?.lifeHack?.components?.weather}
                            onChange={(e) => handleLifeHackComponent('weather', e?.target?.checked)}
                          />
                          <span className="text-sm">Weather</span>
                        </div>
                        <div className="flex items-center space-x-2 p-2 rounded bg-card">
                          <Checkbox
                            checked={componentSelection?.lifeHack?.components?.meal}
                            onChange={(e) => handleLifeHackComponent('meal', e?.target?.checked)}
                          />
                          <span className="text-sm">Meal Planning</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 4f) Meeting Notes (disabled) */}
                  <div className="flex items-start space-x-3 p-3 rounded-lg bg-muted/20 opacity-50">
                    <Checkbox
                     
                      disabled
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h5 className="font-body font-medium text-muted-foreground">
                          Meeting Notes
                        </h5>
                        <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded">
                          Coming Soon
                        </span>
                      </div>
                      <p className="font-caption text-xs text-muted-foreground mt-1">
                        Dedicated space for meeting notes (currently unavailable)
                      </p>
                    </div>
                  </div>

                  {/* 4g) Notes (disabled) */}
                  <div className="flex items-start space-x-3 p-3 rounded-lg bg-muted/20 opacity-50">
                    <Checkbox
                     
                      disabled
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h5 className="font-body font-medium text-muted-foreground">
                          Notes
                        </h5>
                        <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded">
                          Coming Soon
                        </span>
                      </div>
                      <p className="font-caption text-xs text-muted-foreground mt-1">
                        General notes pages (currently unavailable)
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Time Format Selection */}
      {(componentSelection?.dailyPlanner?.enabled && 
        (componentSelection?.dailyPlanner?.schedule || componentSelection?.dailyPlanner?.combination)) && (
        <div className="space-y-4">
          <h3 className="font-heading text-lg font-semibold text-foreground">
            Time Format
          </h3>
          
          <div className="flex flex-wrap gap-4">
            <div
              onClick={() => onTimeFormatChange('24h')}
              className={`
                flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors
                ${timeFormat === '24h' ? 'border-primary bg-primary/5' : 'border-border bg-card'}
              `}
            >
              <div className={`
                w-4 h-4 rounded-full border-2 flex items-center justify-center
                ${timeFormat === '24h' ? 'border-primary bg-primary' : 'border-muted-foreground'}
              `}>
                {timeFormat === '24h' && (
                  <div className="w-2 h-2 rounded-full bg-white" />
                )}
              </div>
              <span className="font-body text-sm">24-hour (00:00)</span>
            </div>

            <div
              onClick={() => onTimeFormatChange('12h-short')}
              className={`
                flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors
                ${timeFormat === '12h-short' ? 'border-primary bg-primary/5' : 'border-border bg-card'}
              `}
            >
              <div className={`
                w-4 h-4 rounded-full border-2 flex items-center justify-center
                ${timeFormat === '12h-short' ? 'border-primary bg-primary' : 'border-muted-foreground'}
              `}>
                {timeFormat === '12h-short' && (
                  <div className="w-2 h-2 rounded-full bg-white" />
                )}
              </div>
              <span className="font-body text-sm">12-hour short (AM/PM)</span>
            </div>
            
            <div
              onClick={() => onTimeFormatChange('12h-long')}
              className={`
                flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors
                ${timeFormat === '12h-long' ? 'border-primary bg-primary/5' : 'border-border bg-card'}
              `}
            >
              <div className={`
                w-4 h-4 rounded-full border-2 flex items-center justify-center
                ${timeFormat === '12h-long' ? 'border-primary bg-primary' : 'border-muted-foreground'}
              `}>
                {timeFormat === '12h-long' && (
                  <div className="w-2 h-2 rounded-full bg-white" />
                )}
              </div>
              <span className="font-body text-sm">12-hour long (12:00 AM)</span>
            </div>
          </div>
        </div>
      )}
      {/* Time Range Selector */}
      {(componentSelection?.dailyPlanner?.enabled && 
        (componentSelection?.dailyPlanner?.schedule || componentSelection?.dailyPlanner?.combination)) && (
        <div className="space-y-4">
          <h3 className="font-heading text-lg font-semibold text-foreground">
            Time Range
          </h3>
          <p className="font-body text-sm text-muted-foreground">
            Select the time range for your daily schedule (minimum 8 hours)
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Start Time"
              options={getStartTimeOptions()}
              value={timeRange?.start !== undefined ? timeRange?.start : 6}
              onChange={handleStartTimeChange}
              placeholder="Select start time"
            />
            
            <Select
              label="End Time"
              options={getEndTimeOptions()}
              value={timeRange?.end !== undefined ? (timeRange?.end === 0 ? 24 : timeRange?.end) : 18}
              onChange={handleEndTimeChange}
              placeholder="Select end time"
            />
          </div>

          {/* Range Error Message */}
          {rangeError && (
            <div className="flex items-start space-x-2 p-3 bg-warning/10 border border-warning rounded-lg">
              <Icon name="AlertCircle" size={16} className="text-warning mt-0.5" />
              <p className="text-sm text-warning">{rangeError}</p>
            </div>
          )}

          {/* Range Info */}
          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <span className="font-body text-sm text-muted-foreground">
              Selected Range:
            </span>
            <span className="font-body text-sm font-semibold text-foreground">
              {formatTime(timeRange?.start)} - {formatTime(timeRange?.end === 0 ? 24 : timeRange?.end)}
              {' '}({Math.abs((timeRange?.end === 0 ? 24 : timeRange?.end) - timeRange?.start)} hours)
            </span>
          </div>
        </div>
      )}
      {/* Additional Sections */}
      <div className="space-y-4">
        <h3 className="font-heading text-lg font-semibold text-foreground">
          Additional Sections
        </h3>
        <p className="font-body text-sm text-muted-foreground">
          Select additional sections you'd like to include in your planner
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {sectionOptions?.map((section) => {
            const isLifeHackSection = ['mood', 'water', 'weather', 'meals']?.includes(section?.id);
            const isDisabled = isLifeHackSection && !componentSelection?.lifeHack?.enabled;
            
            return (
              <div
                key={section?.id}
                className={`flex items-start space-x-3 p-3 rounded-lg border border-border bg-card transition-colors ${
                  isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted/50'
                }`}
              >
                <Checkbox
                  checked={selectedSections?.includes(section?.id)}
                  onChange={(e) => !isDisabled && onSectionToggle(section?.id, e?.target?.checked)}
                  disabled={isDisabled}
                  className="mt-1"
                />
                
                <div className="flex items-start space-x-3 flex-1">
                  <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
                    <Icon name={section?.icon} size={16} className="text-muted-foreground" />
                  </div>
                  
                  <div>
                    <div className="flex items-center space-x-2">
                      <h4 className="font-body font-medium text-foreground">
                        {section?.name}
                      </h4>
                      {isDisabled && (
                        <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded">
                          Requires Life Hack
                        </span>
                      )}
                    </div>
                    <p className="font-caption text-xs text-muted-foreground">
                      {section?.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Habit Tracker Configuration */}
      {selectedSections?.includes('habits') && (
        <div className="space-y-4 border-t border-border pt-6">
          <div className="flex items-center space-x-2">
            <Icon name="CheckSquare" size={20} className="text-primary" />
            <h3 className="font-heading text-lg font-semibold text-foreground">
              Habit Tracker Configuration
            </h3>
          </div>
          <p className="font-body text-sm text-muted-foreground">
            Configure your habits to track throughout the year (1-5 habits recommended)
          </p>
          
          {/* Habit Input Fields */}
          <div className="space-y-3">
            {[1, 2, 3, 4, 5]?.map((habitNum) => {
              const habitData = selectedHabits?.[habitNum - 1] || { name: '', enabled: false };
              const isEnabled = habitData?.enabled;
              const habitName = habitData?.name || '';
              
              return (
                <div key={habitNum} className="flex items-center space-x-3 p-3 rounded-lg border border-border bg-card">
                  <Checkbox
                    checked={isEnabled}
                    onChange={(e) => {
                      const newHabits = [...(selectedHabits || [])];
                      newHabits[habitNum - 1] = {
                        name: habitName,
                        enabled: e?.target?.checked
                      };
                      onHabitsChange?.(newHabits?.filter(h => h?.enabled));
                    }}
                  />
                  
                  <div className="flex-1">
                    <Input
                      placeholder={`Habit ${habitNum} (e.g., "Exercise 30 min daily")`}
                      value={habitName}
                      onChange={(e) => {
                        const newHabits = [...(selectedHabits || [])];
                        newHabits[habitNum - 1] = {
                          name: e?.target?.value,
                          enabled: isEnabled
                        };
                        if (e?.target?.value) {
                          newHabits[habitNum - 1].enabled = true;
                        }
                        onHabitsChange?.(newHabits?.filter(h => h?.enabled && h?.name));
                      }}
                      disabled={!isEnabled && !habitName}
                      className={!isEnabled && !habitName ? 'opacity-50' : ''}
                    />
                  </div>
                  
                  <span className="font-caption text-xs text-muted-foreground whitespace-nowrap">
                    Habit {habitNum}
                  </span>
                </div>
              );
            })}
          </div>
          
          <div className="flex items-start space-x-2 p-3 bg-info/10 border border-info rounded-lg">
            <Icon name="Info" size={16} className="text-info mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-info">
                <strong>Note:</strong> Each selected habit will generate a dedicated year-long tracking page in your planner with weekly checkboxes for all four quarters.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LayoutCustomizer;